import React, { useState, useEffect } from 'react';
import { Tag, Plus, Trash2, Save, Copy, Eye, EyeOff } from 'lucide-react';
import { usePricing, SpecialOffer } from '@context/PricingContext';
import Button from '@components/common/Button';
import Input from '@components/common/Input';
import Alert from '@components/common/Alert';

interface PromoCodeManagerProps {
  onDataChange?: () => void;
  onSaveSuccess?: () => void;
}

const PromoCodeManager: React.FC<PromoCodeManagerProps> = ({ onDataChange, onSaveSuccess }) => {
  const { configuration, loading, updateSpecialOffers } = usePricing();
  const [offers, setOffers] = useState<SpecialOffer[]>([]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    if (configuration?.specialOffers) {
      setOffers([...configuration.specialOffers]);
    }
  }, [configuration?.specialOffers]);

  const handleOfferChange = (
    index: number,
    field: keyof SpecialOffer,
    value: string | number | boolean
  ) => {
    const updatedOffers = offers.map((offer, i) =>
      i === index ? { ...offer, [field]: value } : offer
    );

    setOffers(updatedOffers);
    setSaveSuccess(false);
    onDataChange?.();
  };

  const handleConditionChange = (
    index: number,
    condition: string,
    value: string | number | undefined
  ) => {
    const updatedOffers = offers.map((offer, i) => {
      if (i === index) {
        return {
          ...offer,
          conditions: {
            ...offer.conditions,
            [condition]: value,
          },
        };
      }
      return offer;
    });

    setOffers(updatedOffers);
    setSaveSuccess(false);
    onDataChange?.();
  };

  const addOffer = () => {
    const newOffer: SpecialOffer = {
      id: `promo_${Date.now()}`,
      name: 'SAVE10',
      discountType: 'percentage',
      discountValue: 10,
      isActive: true,
      conditions: {
        minPhotos: 5,
        timeLimit: 48,
      },
    };

    setOffers([...offers, newOffer]);
    setSaveSuccess(false);
    onDataChange?.();
  };

  const removeOffer = (index: number) => {
    const updatedOffers = offers.filter((_, i) => i !== index);
    setOffers(updatedOffers);
    setSaveSuccess(false);
    onDataChange?.();
  };

  const duplicateOffer = (index: number) => {
    const originalOffer = offers[index];
    const duplicatedOffer: SpecialOffer = {
      ...originalOffer,
      id: `promo_${Date.now()}`,
      name: `${originalOffer.name}_COPY`,
      isActive: false,
    };

    setOffers([...offers, duplicatedOffer]);
    setSaveSuccess(false);
    onDataChange?.();
  };

  const handleSave = async () => {
    setSaveLoading(true);
    setSaveError(null);
    setSaveSuccess(false);

    // Validate offers
    const errors = [];
    const promoNames = offers.map(o => o.name.toUpperCase());
    const uniquePromoNames = new Set(promoNames);

    if (promoNames.length !== uniquePromoNames.size) {
      errors.push('Promo code names must be unique');
    }

    if (offers.some(o => !o.name.trim())) {
      errors.push('All promo codes must have a name');
    }

    if (offers.some(o => o.discountValue <= 0)) {
      errors.push('Discount values must be greater than 0');
    }

    if (offers.some(o => o.discountType === 'percentage' && o.discountValue >= 100)) {
      errors.push('Percentage discounts must be less than 100%');
    }

    if (errors.length > 0) {
      setSaveError(errors.join('. '));
      setSaveLoading(false);
      return;
    }

    try {
      await updateSpecialOffers(offers);
      setSaveSuccess(true);
      onSaveSuccess?.();
    } catch (err: any) {
      setSaveError(err.message || 'Failed to save special offers');
    } finally {
      setSaveLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // Could add a toast notification here
  };

  const filteredOffers = showInactive ? offers : offers.filter(o => o.isActive);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Tag className="h-5 w-5 text-orange-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Promo Codes</h2>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="small"
              onClick={() => setShowInactive(!showInactive)}
              icon={showInactive ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            >
              {showInactive ? 'Hide Inactive' : 'Show All'}
            </Button>
            <Button
              variant="ghost"
              size="small"
              onClick={addOffer}
              icon={<Plus className="h-4 w-4" />}
            >
              Add Promo Code
            </Button>
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-1">Create special discount codes for customers.</p>
      </div>

      <div className="p-6 space-y-6">
        {saveError && <Alert type="error" title="Save Failed" message={saveError} />}

        {saveSuccess && (
          <Alert
            type="success"
            title="Saved Successfully"
            message="Your promo codes have been updated."
          />
        )}

        {filteredOffers.length === 0 ? (
          <div className="text-center py-8">
            <Tag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">
              {showInactive ? 'No promo codes found' : 'No active promo codes'}
            </p>
            <Button
              variant="outline"
              onClick={addOffer}
              className="mt-4"
              icon={<Plus className="h-4 w-4" />}
            >
              Create First Promo Code
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredOffers.map((offer, index) => (
              <div
                key={offer.id}
                className={`border rounded-lg p-4 ${offer.isActive ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        offer.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {offer.isActive ? 'Active' : 'Inactive'}
                    </span>
                    <code className="bg-white px-2 py-1 text-sm font-mono border rounded">
                      {offer.name}
                    </code>
                    <Button
                      variant="ghost"
                      size="small"
                      onClick={() => copyToClipboard(offer.name)}
                      icon={<Copy className="h-3 w-3" />}
                    />
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="ghost"
                      size="small"
                      onClick={() => duplicateOffer(index)}
                      icon={<Copy className="h-4 w-4" />}
                    >
                      Duplicate
                    </Button>
                    <Button
                      variant="ghost"
                      size="small"
                      onClick={() => removeOffer(index)}
                      icon={<Trash2 className="h-4 w-4" />}
                      className="text-red-600 hover:bg-red-50"
                    >
                      Delete
                    </Button>
                  </div>
                </div>

                {/* Basic Settings */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <Input
                      label="Promo Code"
                      type="text"
                      value={offer.name}
                      onChange={e => handleOfferChange(index, 'name', e.target.value.toUpperCase())}
                      placeholder="SAVE10"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Discount Type
                    </label>
                    <select
                      value={offer.discountType}
                      onChange={e =>
                        handleOfferChange(
                          index,
                          'discountType',
                          e.target.value as 'percentage' | 'fixed'
                        )
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount ($)</option>
                    </select>
                  </div>

                  <div>
                    <Input
                      label={`Discount ${offer.discountType === 'percentage' ? '%' : '$'}`}
                      type="number"
                      min="1"
                      max={offer.discountType === 'percentage' ? '99' : '999'}
                      value={offer.discountValue.toString()}
                      onChange={e =>
                        handleOfferChange(index, 'discountValue', parseInt(e.target.value) || 0)
                      }
                      placeholder="10"
                    />
                  </div>

                  <div className="flex items-end">
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={offer.isActive}
                        onChange={e => handleOfferChange(index, 'isActive', e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Active</span>
                    </label>
                  </div>
                </div>

                {/* Conditions */}
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">Conditions (Optional)</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Input
                        label="Minimum Photos"
                        type="number"
                        min="1"
                        value={offer.conditions?.minPhotos?.toString() || ''}
                        onChange={e =>
                          handleConditionChange(
                            index,
                            'minPhotos',
                            parseInt(e.target.value) || undefined
                          )
                        }
                        placeholder="5"
                        helperText="Min photos required"
                      />
                    </div>

                    <div>
                      <Input
                        label="Maximum Photos"
                        type="number"
                        min="1"
                        value={offer.conditions?.maxPhotos?.toString() || ''}
                        onChange={e =>
                          handleConditionChange(
                            index,
                            'maxPhotos',
                            parseInt(e.target.value) || undefined
                          )
                        }
                        placeholder="50"
                        helperText="Max photos allowed"
                      />
                    </div>

                    <div>
                      <Input
                        label="Time Limit (Hours)"
                        type="number"
                        min="1"
                        value={offer.conditions?.timeLimit?.toString() || ''}
                        onChange={e =>
                          handleConditionChange(
                            index,
                            'timeLimit',
                            parseInt(e.target.value) || undefined
                          )
                        }
                        placeholder="48"
                        helperText="Hours after event"
                      />
                    </div>
                  </div>
                </div>

                {/* Valid Until */}
                <div className="border-t pt-4 mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Input
                        label="Valid Until (Optional)"
                        type="date"
                        value={offer.validUntil || ''}
                        onChange={e => handleOfferChange(index, 'validUntil', e.target.value)}
                        helperText="Leave empty for no expiration"
                      />
                    </div>
                    <div className="flex items-end text-sm text-gray-600">
                      <div>
                        {offer.discountType === 'percentage'
                          ? `${offer.discountValue}% off total order`
                          : `$${(offer.discountValue / 100).toFixed(2)} off total order`}
                        {offer.conditions?.minPhotos && (
                          <div>Minimum {offer.conditions.minPhotos} photos</div>
                        )}
                        {offer.conditions?.timeLimit && (
                          <div>Valid for {offer.conditions.timeLimit} hours after event</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Usage Tips */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-medium text-blue-900 mb-2">Promo Code Tips</h3>
          <div className="text-sm text-blue-700 space-y-1">
            <div>• Use clear, memorable codes (SAVE20, EARLYBIRD, WEDDING50)</div>
            <div>• Set time limits to create urgency (24-48 hours after event)</div>
            <div>• Use minimum photo requirements to encourage larger purchases</div>
            <div>• Percentage discounts work better for higher-value events</div>
            <div>• Fixed discounts are good for specific promotional campaigns</div>
          </div>
        </div>

        {filteredOffers.length > 0 && (
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              loading={saveLoading}
              disabled={loading}
              icon={<Save className="h-4 w-4" />}
            >
              Save Promo Codes
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromoCodeManager;
